import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Quill, { Delta } from 'quill';

import { sanitizeEmptyContent } from '@/shared/util';

import { FORMATS, LIST_PATTERN } from './editor.constant';
import { useQuillCharacterLimit } from './use-quill-character-limit.hook';
import {
  calculateEditedText,
  cleanHtmlWithLists,
  convertNewlinesToBrTags,
  convertTextBeforeListToBulletPoints,
  convertTextToBulletPoints,
  createQuillConfig,
  getCleanedHtmlFromQuill,
  hasContentChanged,
  isHtmlContent,
  isWellFormedHtml,
  registerClipboardMatchers,
  updateQuillContentWithSelection,
} from './util';

interface UseQuillEditorOptions {
  displayAsBullets?: boolean;
  isReadOnly?: boolean;
  maxLength?: number;
  onBlur?: (editedText: string, currentText?: string) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  value?: string;
}

export const useQuillEditor = ({
  displayAsBullets = false,
  isReadOnly = false,
  maxLength,
  onBlur,
  onChange,
  placeholder = '',
  value,
}: UseQuillEditorOptions) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const quillRef = useRef<null | Quill>(null);
  const sanitizedValue = sanitizeEmptyContent(value || '');
  const [initialValue, setInitialValue] = useState(sanitizedValue);
  const lastPropValueRef = useRef<string | undefined>(sanitizedValue);
  const isUserEditingRef = useRef(false);

  const {
    characterCount,
    enforceMaxLengthIfNeeded,
    getCurrentCharacterCount,
    isFocused,
    registerClipboardMatcher,
    setCharacterCount,
    setIsFocused,
    syncCharacterCount,
  } = useQuillCharacterLimit({ maxLength, quillRef });

  const htmlValue = useMemo(() => {
    if (isWellFormedHtml(sanitizedValue)) {
      if (LIST_PATTERN.test(sanitizedValue)) {
        return cleanHtmlWithLists(sanitizedValue);
      }

      return sanitizedValue;
    }

    if (isReadOnly && !displayAsBullets) {
      if (!isHtmlContent(sanitizedValue)) {
        return convertNewlinesToBrTags(sanitizedValue);
      }

      if (LIST_PATTERN.test(sanitizedValue)) {
        const cleaned = cleanHtmlWithLists(sanitizedValue);

        return convertTextBeforeListToBulletPoints(cleaned);
      }

      return sanitizedValue;
    }

    return convertTextToBulletPoints(sanitizedValue);
  }, [displayAsBullets, isReadOnly, sanitizedValue]);

  const handleTextChange = useCallback(
    (changeDelta: Delta) => {
      if (!quillRef.current) {
        return;
      }

      isUserEditingRef.current = true;

      if (typeof maxLength === 'number') {
        const nextCharacterCount = getCurrentCharacterCount();

        const didEnforceMaxLength = enforceMaxLengthIfNeeded(nextCharacterCount, changeDelta);

        if (!didEnforceMaxLength) {
          setCharacterCount(nextCharacterCount);
        }
      }

      if (!onChange) {
        return;
      }

      const validHtml = getCleanedHtmlFromQuill(quillRef.current);

      lastPropValueRef.current = validHtml;
      onChange(validHtml);
    },
    [enforceMaxLengthIfNeeded, getCurrentCharacterCount, maxLength, onChange, setCharacterCount],
  );

  const handleBlurNormal = useCallback(
    (currentValue: string) => {
      if (!onBlur) {
        return;
      }

      const sanitizedCurrentValue = sanitizeEmptyContent(currentValue);
      const sanitizedPropValue = sanitizeEmptyContent(value || '');
      const editedText = calculateEditedText(
        initialValue || '',
        sanitizedCurrentValue,
        sanitizedPropValue,
      );

      onBlur(editedText, sanitizedCurrentValue);
    },
    [onBlur, initialValue, value],
  );

  const handleSelectionChange = useCallback(
    (range: unknown, oldRange: unknown) => {
      setIsFocused(range !== null);

      if (range !== null || oldRange === null || !onBlur || !quillRef.current) {
        return;
      }

      const rawValue = quillRef.current.root.innerHTML || '';
      const currentValue = sanitizeEmptyContent(rawValue);

      handleBlurNormal(currentValue);
    },
    [onBlur, handleBlurNormal, setIsFocused],
  );

  const initializeQuill = useCallback(() => {
    if (!editorRef.current || quillRef.current) {
      return;
    }

    const config = createQuillConfig({
      formats: FORMATS,
      isReadOnly,
      placeholder,
    });

    quillRef.current = new Quill(editorRef.current, { ...config, bounds: editorRef.current });

    registerClipboardMatchers(quillRef.current);
    registerClipboardMatcher(quillRef.current);

    const html = quillRef.current.clipboard.convert({
      html: htmlValue,
    });

    quillRef.current.setContents(html);
    setInitialValue(quillRef.current.root.innerHTML || '');
    syncCharacterCount();
  }, [htmlValue, isReadOnly, placeholder, registerClipboardMatcher, syncCharacterCount]);

  const updateQuillContent = useCallback(() => {
    if (!quillRef.current) {
      return;
    }

    const isExternalUpdate = sanitizedValue !== lastPropValueRef.current;

    if (isExternalUpdate) {
      lastPropValueRef.current = sanitizedValue;
      isUserEditingRef.current = false;
    }

    if (isUserEditingRef.current) {
      return;
    }

    if (!hasContentChanged(quillRef.current, htmlValue)) {
      return;
    }

    updateQuillContentWithSelection(quillRef.current, htmlValue);

    if (isExternalUpdate) {
      setInitialValue(quillRef.current.root.innerHTML || '');
      syncCharacterCount();
    }
  }, [htmlValue, sanitizedValue, syncCharacterCount]);

  useEffect(() => {
    initializeQuill();
  }, [initializeQuill]);

  useEffect(() => {
    if (quillRef.current) {
      quillRef.current.enable(!isReadOnly);
    }
  }, [isReadOnly]);

  useEffect(() => {
    updateQuillContent();
  }, [updateQuillContent]);

  useEffect(() => {
    if (!quillRef.current) {
      return undefined;
    }

    quillRef.current.on('text-change', handleTextChange);
    quillRef.current.on('selection-change', handleSelectionChange);

    return () => {
      quillRef.current?.off('text-change', handleTextChange);
      quillRef.current?.off('selection-change', handleSelectionChange);
    };
  }, [handleTextChange, handleSelectionChange]);

  const blur = useCallback(() => {
    quillRef.current?.blur();
  }, []);

  return {
    blur,
    characterCount,
    editorRef,
    isFocused,
  };
};
